/**
 * PublisherArticleView — canonical read page for a Publisher article.
 *
 * Route: /article/:slug (mounted for blogs.socialpulse.com)
 * Data:  GET /api/publisher/v1/widget/article/{slug}?site_id=blogspulse
 *
 * Renders title, cover, HTML body, and the ChatPulse discussion
 * anchored to this article's ID.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, User as UserIcon } from "lucide-react";
import ChatPulseThread from "@/components/chatpulse/ChatPulseThread";
import ListenToArticle from "@/components/publisher/ListenToArticle";
import ClapButton from "@/components/publisher/ClapButton";
import FollowWriterButton from "@/components/publisher/FollowWriterButton";
import ReadTimeChip, { estimateMinutes } from "@/components/publisher/ReadTimeChip";
import SocialCapitalChip from "@/components/publisher/SocialCapitalChip";
import ReadingProgressBar from "@/components/publisher/ReadingProgressBar";
import ShareMenu from "@/components/publisher/ShareMenu";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

function timeAgo(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "long", day: "numeric", year: "numeric",
    });
  } catch { return ""; }
}

export default function PublisherArticleView() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, article: null, body_html: null, error: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${BASE}/widget/article/${slug}?site_id=blogspulse`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (alive) setState({ loading: false, article: d.article, body_html: d.body_html || d.article.body_html || null, error: null });
      } catch (e) {
        if (alive) setState({ loading: false, article: null, body_html: null, error: e.message });
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  if (state.loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center flex items-center justify-center gap-2 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading article…
      </div>
    );
  }

  if (state.error || !state.article) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center text-slate-500">
        <p className="text-lg font-semibold text-slate-900 mb-2">Article not found</p>
        <p className="text-sm mb-4">The slug <code className="bg-slate-100 px-1 rounded">{slug}</code> doesn't match any published article.</p>
        <Link to="/publisher/notes" className="text-blue-700 hover:underline text-sm inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Back to Publisher
        </Link>
      </div>
    );
  }

  const a = state.article;
  const canonical = state.canonical_url || `${window.location.origin}/blog/${a.slug}`;
  return (
    <article className="max-w-3xl mx-auto px-4 lg:px-6 py-8" data-testid="publisher-article-view">
      <ReadingProgressBar targetSelector="[data-testid='publisher-article-view']" />
      <ArticleMeta article={a} canonicalUrl={canonical} />
      <header className="mb-6 pb-5 border-b border-slate-200">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold flex items-center gap-1.5 mb-1">
          <span>{a.primary_category}</span>
          {a.editorial_label && (
            <>
              <span className="text-slate-300">·</span>
              <span>{a.editorial_label}</span>
            </>
          )}
          {a.primary_location_id && (
            <>
              <span className="text-slate-300">·</span>
              <span>{a.primary_location_id}</span>
            </>
          )}
        </div>
        <h1
          className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-tight"
          data-testid="publisher-article-title"
        >
          {a.title}
        </h1>
        {a.summary && (
          <p className="mt-3 text-base text-slate-600 leading-relaxed">{a.summary}</p>
        )}
        {(a.author_id || a.published_at) && (
          <div className="mt-3 flex items-center gap-3 flex-wrap text-xs text-slate-500">
            {a.author_id && <AuthorLink writerId={a.author_id} />}
            {a.published_at && <time>{timeAgo(a.published_at)}</time>}
            <ReadTimeChip html={state.body_html} testId="publisher-article-read-time" />
            {a.author_id && (
              <SocialCapitalChip writerId={a.author_id} variant="inline" testId="publisher-article-capital" />
            )}
            {a.author_id && (
              <FollowWriterButton
                writerId={a.author_id}
                size="sm"
                testIdPrefix="publisher-article-follow"
              />
            )}
            <ShareMenu
              url={canonical}
              title={a.title}
              summary={a.summary || ""}
              size="sm"
              testIdPrefix="publisher-article-share"
            />
          </div>
        )}
      </header>

      {a.cover_image_url && (
        <img
          src={a.cover_image_url}
          alt=""
          className="w-full rounded-2xl mb-6 border border-slate-200"
        />
      )}

      <ListenToArticle slug={slug} />

      {state.body_html ? (
        <div
          className="pub-prose prose prose-slate max-w-none text-base leading-relaxed [&_p]:my-4 [&_a]:text-blue-700 [&_a]:underline [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-6 [&_ul]:list-disc [&_ul]:ml-5 [&_ol]:list-decimal [&_ol]:ml-5"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: state.body_html }}
          data-testid="publisher-article-body"
        />
      ) : (
        <p className="italic text-slate-500">
          Full text is available on partner sites — check the widgets embedded on
          NewsPulse, FarmersPulse, and FoodPulse.
        </p>
      )}

      {/* ChatPulse — anchored to the article's ID so the same thread
          surfaces wherever the article is embedded. */}
      <ChatPulseThread
        contextType="article"
        contextId={a.id}
        contextLabel={a.title}
      />

      {/* Reader reactions — Medium-style clap bar. Sticky at bottom on
          mobile so it's always in thumb-reach; inline on desktop. */}
      <div
        className="mt-8 pt-6 border-t border-slate-200 flex items-center justify-center gap-4"
        data-testid="publisher-article-clap-bar"
      >
        <ClapButton
          slug={slug}
          initialTotal={a.total_claps || 0}
          size="lg"
          testIdPrefix="publisher-article-clap"
        />
      </div>
    </article>
  );
}

/** Resolves a writer's UUID to their display name via /writers/{id}/summary.
 *  Renders a UUID slice while loading; swaps to the real name on hydrate. */
function AuthorLink({ writerId }) {
  const [name, setName] = useState(null);
  useEffect(() => {
    if (!writerId) return;
    let alive = true;
    const base = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;
    fetch(`${base}/writers/${writerId}/summary`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (alive && d) setName(d.display_name); })
      .catch(() => {});
    return () => { alive = false; };
  }, [writerId]);
  return (
    <Link
      to={`/writers/${writerId}`}
      className="inline-flex items-center gap-1 hover:text-teal-700 font-medium"
      data-testid="publisher-article-author"
    >
      <UserIcon className="w-3 h-3" />
      {name || `${writerId.slice(0, 8)}…`}
    </Link>
  );
}


/** Manages the <head> tags so shares on WhatsApp/Twitter/LinkedIn
 *  pick up the auto-generated OG card. We inject on mount and clean
 *  up on unmount so client-side navigation doesn't leak stale tags. */
function ArticleMeta({ article, canonicalUrl }) {
  useEffect(() => {
    if (!article) return;
    const BASE = process.env.REACT_APP_BACKEND_URL || "";
    const ogUrl = `${BASE}/api/publisher/v1/articles/${article.slug}/og-image.png`;
    const prevTitle = document.title;
    document.title = `${article.title} · SocialPulse Blog`;
    const tags = [
      ["property", "og:title",       article.title],
      ["property", "og:description", article.summary || ""],
      ["property", "og:image",       ogUrl],
      ["property", "og:type",        "article"],
      ["property", "og:url",         canonicalUrl || window.location.href],
      ["name",     "twitter:card",   "summary_large_image"],
      ["name",     "twitter:title",  article.title],
      ["name",     "twitter:description", article.summary || ""],
      ["name",     "twitter:image",  ogUrl],
      ["name",     "description",    article.summary || ""],
    ];
    const created = [];
    for (const [attr, key, val] of tags) {
      if (!val) continue;
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      const wasPresent = !!el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", val);
      created.push({ el, wasPresent, prev });
    }
    return () => {
      document.title = prevTitle;
      created.forEach(({ el, wasPresent, prev }) => {
        if (!wasPresent) el.remove();
        else if (prev != null) el.setAttribute("content", prev);
      });
    };
  }, [article, canonicalUrl]);
  return null;
}
